import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Autocomplete,
  TextField,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  Divider,
} from "@mui/material";
import {
  getCpSourcingSourceUsers,
  getCpSourcingRecipientUsers,
  getEligibleCpsForSourcingManager,
  executeCpSourcingManagerChangeFlow,
} from "../../Services/WorkTransferApis";
import ReviewDialog from "../../Components/WorkTransfer/ReviewDialog";
import WorkTransferNotice from "../../Components/WorkTransfer/WorkTransferNotice";
import { COLORS, PageHeader, Field, PrimaryButton, fieldSx, cardSx } from "../../Components/WorkTransfer/wtStyle";

// Matches ChannelPartner.jsx's own cpType filter options (Components/
// ChannelPartner/ChannelPartner.jsx) so this reuses the same vocabulary
// instead of inventing a new one.
const CP_TYPE_OPTIONS = ["Entity", "Individual"];

// "Update CP Sourcing Manager" — a CP Sourcing Manager left the org (or is
// overloaded); reassign CP.cpSourceId for a batch of CPs to a new manager.
// No lead/approval involvement at all (see cpSourcingManagerFlow.js) — this
// only ever touches CP rows. Picker design (task #1, locked): approved CPs
// only, searchable, select-all + individual, filterable by cpType and
// created_at range. Business rules (task #3): reason required (enforced
// server-side for every mode), no bulk minimum, no extra permission gate.
export default function CpSourcingReassignPage({ canExecute = false }) {
  const [sourceUsers, setSourceUsers] = useState([]);
  const [recipientUsers, setRecipientUsers] = useState([]);
  const [manager, setManager] = useState(null);
  const [newManager, setNewManager] = useState(null);

  const [search, setSearch] = useState("");
  const [cpTypeFilter, setCpTypeFilter] = useState([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [loadingCps, setLoadingCps] = useState(false);
  const [cps, setCps] = useState(null);
  const [selectedCpIds, setSelectedCpIds] = useState([]);

  const [reason, setReason] = useState("");
  const [reviewOpen, setReviewOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    getCpSourcingSourceUsers()
      .then((res) => setSourceUsers(res.success ? res.data.users : []))
      .catch(() => setNotice({ type: "error", title: "Couldn't load managers", message: "Try reloading the page." }));
    getCpSourcingRecipientUsers()
      .then((res) => setRecipientUsers(res.success ? res.data.users : []))
      .catch(() => setNotice({ type: "error", title: "Couldn't load managers", message: "Try reloading the page." }));
  }, []);

  const loadCps = async () => {
    if (!manager) {
      setNotice({ type: "warning", title: "Pick a sourcing manager first", message: "Select the manager whose CPs you're reassigning." });
      return;
    }
    setLoadingCps(true);
    setCps(null);
    setSelectedCpIds([]);
    try {
      const res = await getEligibleCpsForSourcingManager({ userId: manager.userId, cpType: cpTypeFilter, dateFrom, dateTo, search });
      if (!res.success) {
        setNotice({ type: "error", title: "Couldn't load CPs", message: res.message });
        return;
      }
      setCps(res.data.cps);
      if (res.data.cps.length === 0) {
        setNotice({ type: "warning", title: "No CPs found", message: `${manager.name} has no approved CPs matching these filters.` });
      }
    } catch (err) {
      setNotice({ type: "error", title: "Couldn't load CPs", message: err.message });
    } finally {
      setLoadingCps(false);
    }
  };

  const toggleCp = (cpId) => {
    setSelectedCpIds((prev) => (prev.includes(cpId) ? prev.filter((id) => id !== cpId) : [...prev, cpId]));
  };

  const allSelected = cps?.length > 0 && selectedCpIds.length === cps.length;
  const someSelected = selectedCpIds.length > 0 && !allSelected;
  const toggleSelectAll = () => {
    setSelectedCpIds(allSelected ? [] : (cps || []).map((c) => c.cpId));
  };

  const openReview = () => {
    if (!canExecute) {
      setNotice({ type: "error", title: "View only", message: "Your role can view this, but doesn't have permission to confirm a transfer." });
      return;
    }
    if (selectedCpIds.length === 0) {
      setNotice({ type: "warning", title: "Select at least one CP", message: "Tick which CPs are moving to the new sourcing manager." });
      return;
    }
    if (!newManager) {
      setNotice({ type: "warning", title: "Choose a new sourcing manager", message: "Select who these CPs move under." });
      return;
    }
    if (newManager.userId === manager.userId) {
      setNotice({ type: "warning", title: "Same manager", message: "The new sourcing manager must be different from the current one." });
      return;
    }
    setReviewOpen(true);
  };

  const selectedNames = (cps || []).filter((c) => selectedCpIds.includes(c.cpId)).map((c) => c.billingName);
  const summaryLines = [
    `Moving ${selectedCpIds.length} CP(s) from ${manager?.name} to ${newManager?.name}:`,
    selectedNames.join(", "),
    "Future approvals for these CPs resolve live via the new sourcing manager — no separate approval queue needs updating.",
  ];

  const confirmTransfer = async () => {
    setConfirming(true);
    try {
      const performedBy = JSON.parse(localStorage.getItem("user"))?.userId;
      const res = await executeCpSourcingManagerChangeFlow({
        fromUserId: manager.userId,
        toUserId: newManager.userId,
        cpIds: selectedCpIds,
        reason,
        performedBy,
      });
      if (!res.success) {
        setNotice({ type: "error", title: "Transfer failed", message: res.message });
        return;
      }
      setReviewOpen(false);
      setNotice({ type: "success", title: "CPs reassigned", message: `${res.data.operationsApplied} CP(s) moved.` });
      setCps(null);
      setSelectedCpIds([]);
      setNewManager(null);
      setReason("");
    } catch (err) {
      setNotice({ type: "error", title: "Transfer failed", message: err.message });
    } finally {
      setConfirming(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 760 }}>
      <PageHeader
        title="Update CP Sourcing Manager"
        subtitle="A sourcing manager left, or is overloaded — search their CPs and bulk reassign to someone else."
      />

      <Box sx={{ ...cardSx, p: 2, mb: 2 }}>
        <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap", alignItems: "flex-end" }}>
          <Field label="Current sourcing manager" sx={{ width: 260 }}>
            <Autocomplete
              options={sourceUsers}
              getOptionLabel={(u) => u.name || ""}
              value={manager}
              onChange={(e, val) => setManager(val)}
              renderInput={(params) => <TextField {...params} placeholder="Choose a manager" />}
              sx={fieldSx}
            />
          </Field>
          <Field label="Search CPs" sx={{ width: 200 }}>
            <TextField placeholder="Name or CP ID" value={search} onChange={(e) => setSearch(e.target.value)} sx={fieldSx} />
          </Field>
          <Field label="CP type" sx={{ width: 200 }}>
            <Autocomplete
              multiple
              options={CP_TYPE_OPTIONS}
              value={cpTypeFilter}
              onChange={(e, val) => setCpTypeFilter(val)}
              renderInput={(params) => <TextField {...params} placeholder="Any type" />}
              sx={fieldSx}
            />
          </Field>
          <Field label="Created from">
            <TextField type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} sx={fieldSx} />
          </Field>
          <Field label="Created to">
            <TextField type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} sx={fieldSx} />
          </Field>
          <PrimaryButton onClick={loadCps} disabled={loadingCps}>
            {loadingCps ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Load CPs"}
          </PrimaryButton>
        </Box>
      </Box>

      {cps?.length > 0 && (
        <Box sx={{ ...cardSx, p: 2, mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
            <Typography fontSize={13} fontWeight={700}>
              CPs — tick who's moving ({selectedCpIds.length}/{cps.length} selected)
            </Typography>
          </Box>
          <List dense disablePadding>
            <ListItem button onClick={toggleSelectAll} sx={{ cursor: "pointer", borderBottom: `1px solid ${COLORS.border}` }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <Checkbox edge="start" checked={allSelected} indeterminate={someSelected} onChange={toggleSelectAll} />
              </ListItemIcon>
              <ListItemText primaryTypographyProps={{ fontSize: 13, fontWeight: 600 }} primary="Select all" />
            </ListItem>
            {cps.map((c) => (
              <ListItem
                key={c.cpId}
                button
                onClick={() => toggleCp(c.cpId)}
                sx={{ cursor: "pointer", borderBottom: `1px solid ${COLORS.border}`, "&:last-child": { borderBottom: "none" } }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Checkbox edge="start" checked={selectedCpIds.includes(c.cpId)} onChange={() => toggleCp(c.cpId)} />
                </ListItemIcon>
                <ListItemText
                  primaryTypographyProps={{ fontSize: 14 }}
                  primary={c.billingName}
                  secondaryTypographyProps={{ fontSize: 11.5, color: COLORS.textSecondary }}
                  secondary={[c.cpDisplayId, c.cpType, c.created_at ? new Date(c.created_at).toLocaleDateString() : null].filter(Boolean).join(" · ")}
                />
              </ListItem>
            ))}
          </List>

          <Divider sx={{ my: 2 }} />

          <Typography fontSize={13} fontWeight={700} sx={{ mb: 1 }}>
            New sourcing manager
          </Typography>
          <Field label="Assign CPs to" sx={{ maxWidth: 320 }}>
            <Autocomplete
              options={recipientUsers.filter((u) => u.userId !== manager?.userId)}
              getOptionLabel={(u) => u.name || ""}
              value={newManager}
              onChange={(e, val) => setNewManager(val)}
              renderInput={(params) => <TextField {...params} placeholder="Choose new manager" />}
              sx={fieldSx}
            />
          </Field>

          <Box sx={{ mt: 2.5 }}>
            <PrimaryButton onClick={openReview} disabled={!canExecute} title={!canExecute ? "Your role has view-only access to this feature" : undefined}>
              Review
            </PrimaryButton>
            {!canExecute && (
              <Typography fontSize={12} color={COLORS.textSecondary} sx={{ mt: 1 }}>
                Your role has view-only access — you can browse CPs, but can't confirm a reassignment.
              </Typography>
            )}
          </Box>
        </Box>
      )}

      <ReviewDialog
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        summaryLines={summaryLines}
        reason={reason}
        setReason={setReason}
        distribution="single"
        setDistribution={() => {}}
        allowRoundRobin={false}
        onConfirm={confirmTransfer}
        confirming={confirming}
      />

      <WorkTransferNotice notice={notice} onClose={() => setNotice(null)} />
    </Box>
  );
}
